import { ReactElement, ReactNode, useState } from 'react'
import { GroupTable, GroupTableColumn, ProgressBar, TipoBadge } from '../../../components/SalesReport'
import { AccessMetrics, AccessReportDetail, AccessReportGroup, AccessReportResponse } from '../../../hooks/access-report'
import { groupByLabel } from '../filters'
import {
  attendanceTone,
  formatAttendance,
  formatCount,
  formatDayTime,
  groupPill,
  MetricSort,
  nextSort,
  optionalCents,
  sortGroups,
} from '../report'
import { AttendanceValue } from './sections'
import * as S from './styles'

const FOOTNOTE =
  'Pendentes = ingressos emitidos que ainda não tiveram leitura válida. "Metade às" mostra quando o grupo atingiu 50% dos seus emitidos.'

const count = (value: number | null): ReactNode => (value === null ? <S.Muted>—</S.Muted> : formatCount(value))
const time = (value: string | null | undefined): ReactNode => (value == null ? <S.Muted>—</S.Muted> : formatDayTime(value))

const column = (
  id: string,
  label: string,
  render: (values: AccessMetrics) => ReactNode,
  minWidth = 96,
  align: 'left' | 'right' = 'right'
): GroupTableColumn<AccessMetrics> => ({ id, label, width: `minmax(${minWidth}px, 1fr)`, minWidth, align, render })

export const detailColumns = (showPending: boolean, showValues: boolean): Array<GroupTableColumn<AccessMetrics>> =>
  [
    column('issued', 'Emitidos', (v) => count(v.issued), 88),
    column('entries', 'Entradas', (v) => formatCount(v.entries), 88),
    showPending ? column('pending', 'Pendentes', (v) => count(v.pending), 92) : null,
    column('attendance', 'Comparecimento', (v) => <AttendanceValue attendance={v.attendance} />, 150, 'left'),
    showValues ? column('revenue', 'Receita dos acessos', (v) => optionalCents(v.revenueCents), 140) : null,
    showValues ? column('unused', 'Receita não utilizada', (v) => optionalCents(v.unusedRevenueCents), 150) : null,
    column('peak', 'Pico/min', (v) => count(v.peakMinute?.entries ?? null), 84),
    column('peakAt', 'Horário do pico', (v) => time(v.peakMinute?.at), 124),
    column('halfAt', 'Metade às', (v) => time(v.halfAt), 112),
    column('firstAt', '1ª entrada', (v) => time(v.firstAt), 112),
    column('lastAt', 'Última', (v) => time(v.lastAt), 112),
  ].filter((item): item is GroupTableColumn<AccessMetrics> => item !== null)

const Pill = ({ metrics }: { metrics: Pick<AccessMetrics, 'issued' | 'entries'> }): ReactElement | null => {
  const pill = groupPill(metrics)
  return pill === null ? null : <S.Pill>{pill}</S.Pill>
}

function DetailTable({
  group,
  details,
  showPending,
  showValues,
}: {
  group: AccessReportGroup
  details: AccessReportDetail[]
  showPending: boolean
  showValues: boolean
}): ReactElement {
  return (
    <S.SubTable aria-label={`Ingressos e lotes de ${group.label}`}>
      <thead>
        <tr>
          <th scope="col">Ingresso</th>
          <th scope="col">Lote</th>
          <th scope="col">Tipo</th>
          {showValues && <th scope="col">Valor unit.</th>}
          <th scope="col">Emitidos</th>
          <th scope="col">Entradas</th>
          {showPending && <th scope="col">Pendentes</th>}
          <th scope="col">Comparecimento</th>
          {showValues && <th scope="col">Receita dos acessos</th>}
          <th scope="col">1ª entrada</th>
          <th scope="col">Última</th>
        </tr>
      </thead>
      <tbody>
        {details.map((detail) => (
          <tr key={detail.key}>
            <td>{detail.ticket}</td>
            <td>{detail.lot}</td>
            <td>
              <TipoBadge ticketType={detail.type === 'M' ? 'M' : 'I'} courtesy={detail.type === 'C'} compact />
            </td>
            {showValues && <td>{optionalCents(detail.unitValueCents)}</td>}
            <td>{count(detail.issued)}</td>
            <td>{formatCount(detail.entries)}</td>
            {showPending && <td>{count(detail.pending)}</td>}
            <td>
              <AttendanceValue attendance={detail.attendance} />
            </td>
            {showValues && <td>{optionalCents(detail.revenueCents)}</td>}
            <td>{time(detail.firstAt)}</td>
            <td>{time(detail.lastAt)}</td>
          </tr>
        ))}
      </tbody>
    </S.SubTable>
  )
}

interface DetailSectionProps {
  report: AccessReportResponse
  mobile: boolean
}

export function DetailSection({ report, mobile }: DetailSectionProps): ReactElement {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [sort, setSort] = useState<MetricSort | null>(null)
  const { groups, totals, showValues } = report
  const { showPending, groupBy } = report.appliedFilters
  const label = groupByLabel(groupBy)
  const hasDetails = groups.some((group) => (group.details?.length ?? 0) > 0)
  const allExpanded = groups.length > 0 && groups.every((group) => expanded.has(group.key))

  const toggle = (key: string): void => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const toggleAll = (): void => setExpanded(allExpanded ? new Set() : new Set(groups.map((group) => group.key)))
  const sorted = sortGroups(groups, sort)

  return (
    <S.Section aria-label={`Detalhamento por ${label.toLowerCase()}`}>
      <S.DetailHead>
        <S.SectionTitle>{`Detalhamento por ${label.toLowerCase()}`}</S.SectionTitle>
      </S.DetailHead>
      {groups.length === 0 ? (
        <S.Footnote>Nenhum ingresso encontrado com os filtros atuais.</S.Footnote>
      ) : mobile ? (
        <S.MobileGroups>
          {hasDetails && groups.length > 1 && (
            <S.ToggleAll type="button" onClick={toggleAll}>
              {allExpanded ? 'Recolher todos' : 'Expandir todos'}
            </S.ToggleAll>
          )}
          {[...sorted, null].map((group) => {
            const metrics: AccessMetrics = group ?? totals
            const key = group?.key ?? 'total'
            const open = group !== null && expanded.has(key)
            const header = (
              <>
                <S.MobileCardHead>
                  <S.MobileCardTitle>
                    {group === null ? 'Total' : group.context ? `${group.label} · ${group.context}` : group.label}
                  </S.MobileCardTitle>
                  <S.MobileCardValue>{formatAttendance(metrics.attendance)}</S.MobileCardValue>
                </S.MobileCardHead>
                {metrics.attendance !== null && (
                  <ProgressBar
                    percentage={metrics.attendance * 100}
                    track="neutral"
                    tone={attendanceTone(metrics.attendance)}
                    label={`Comparecimento ${formatAttendance(metrics.attendance)}`}
                  />
                )}
                <Pill metrics={metrics} />
                <S.MetricGrid>
                  <div>
                    <dt>Entradas</dt>
                    <dd>{formatCount(metrics.entries)}</dd>
                  </div>
                  <div>
                    <dt>Emitidos</dt>
                    <dd>{count(metrics.issued)}</dd>
                  </div>
                  {showPending && (
                    <div>
                      <dt>Pendentes</dt>
                      <dd>{count(metrics.pending)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Pico/min</dt>
                    <dd>{metrics.peakMinute === null ? '—' : `${metrics.peakMinute.entries} · ${formatDayTime(metrics.peakMinute.at)}`}</dd>
                  </div>
                  <div>
                    <dt>Metade às</dt>
                    <dd>{time(metrics.halfAt)}</dd>
                  </div>
                  <div>
                    <dt>1ª · Última</dt>
                    <dd>{`${formatDayTime(metrics.firstAt)} · ${formatDayTime(metrics.lastAt)}`}</dd>
                  </div>
                  {showValues && (
                    <div>
                      <dt>Receita dos acessos</dt>
                      <dd>{optionalCents(metrics.revenueCents)}</dd>
                    </div>
                  )}
                  {showValues && (
                    <div>
                      <dt>Não utilizada</dt>
                      <dd>{optionalCents(metrics.unusedRevenueCents)}</dd>
                    </div>
                  )}
                </S.MetricGrid>
              </>
            )
            const details = group?.details ?? []
            return (
              <S.MobileCard key={key}>
                {details.length > 0 ? (
                  <S.MobileCardButton type="button" aria-expanded={open} onClick={() => toggle(key)}>
                    {header}
                  </S.MobileCardButton>
                ) : (
                  header
                )}
                {open && (
                  <S.MobileDetail>
                    {details.map((detail) => (
                      <S.MetricGrid key={detail.key}>
                        <div>
                          <dt>Ingresso · Lote</dt>
                          <dd>{`${detail.ticket} · ${detail.lot}`}</dd>
                        </div>
                        <div>
                          <dt>Tipo</dt>
                          <dd>
                            <TipoBadge ticketType={detail.type === 'M' ? 'M' : 'I'} courtesy={detail.type === 'C'} compact />
                          </dd>
                        </div>
                        <div>
                          <dt>Entradas de emitidos</dt>
                          <dd>{`${formatCount(detail.entries)} de ${detail.issued === null ? '—' : formatCount(detail.issued)}`}</dd>
                        </div>
                        <div>
                          <dt>Comparecimento</dt>
                          <dd>{formatAttendance(detail.attendance)}</dd>
                        </div>
                        {showValues && (
                          <div>
                            <dt>Receita dos acessos</dt>
                            <dd>{optionalCents(detail.revenueCents)}</dd>
                          </div>
                        )}
                      </S.MetricGrid>
                    ))}
                  </S.MobileDetail>
                )}
              </S.MobileCard>
            )
          })}
        </S.MobileGroups>
      ) : (
        <GroupTable<AccessMetrics>
          groupLabel={label}
          ariaLabel={`Detalhamento por ${label.toLowerCase()}`}
          columns={detailColumns(showPending, showValues)}
          groups={sorted.map((group) => ({
            key: group.key,
            label: group.context ? `${group.label} · ${group.context}` : group.label,
            badge: <Pill metrics={group} />,
            values: group,
            expanded: expanded.has(group.key),
            onToggle: group.details !== undefined ? () => toggle(group.key) : undefined,
            detail:
              group.details !== undefined ? (
                <DetailTable group={group} details={group.details} showPending={showPending} showValues={showValues} />
              ) : undefined,
          }))}
          total={{ label: 'Total', values: totals }}
          sort={sort}
          onSort={(key) => setSort(nextSort(sort, key))}
          allExpanded={allExpanded}
          onToggleAll={hasDetails ? toggleAll : undefined}
        />
      )}
      <S.Footnote>{FOOTNOTE}</S.Footnote>
    </S.Section>
  )
}
